import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { TextInput, TouchableOpacity, StyleSheet, Text, View } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  runOnJS,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated'
import { heightOfTopNav } from '../../Navigator/TopNavigator/TopNavigator'
import { Search, X } from 'lucide-react-native'
import { useAppContext } from '../API/Context-API/AppContext'
import ViewSmooth from '../View/ViewSmooth'
import BackDrop, { handleBackDrop } from './Components/BackDrop'
import { useSmartNavigate } from '../API/Context-API/hooks/useSmartNavigate'

export interface handleSearchBarTop {
  enable: () => any
  disable: () => any
}

interface SearchBarTopProps {
  onClose: () => void
  navigation: any
  callBackOnPress?: () => any
}

const ANIM_DUR = 260

const SearchBarTop = React.forwardRef<handleSearchBarTop, SearchBarTopProps>(
  ({ onClose, navigation, callBackOnPress }, ref) => {
    const {
      currentWidth,
      isSmallVersion,
      isMediumVersion,
      setSearchBarTopActive,
    } = useAppContext()

    const [query, setQuery] = useState('')
    const [enabled, setEnabled] = useState(false)
    const [render, setRender] = useState(false)

    const [error, setError] = useState<false | { message: string }>(false)
    const errorColor = 'red'

    const refBackDrop = useRef<handleBackDrop>(null)
    const refTextInput = useRef<TextInput>(null)

    const scaleFactor = isSmallVersion ? 0.8 : isMediumVersion ? 1.2 : 1.5
    const tr = useMemo(() => -heightOfTopNav(isSmallVersion), [isSmallVersion])

    // 0 = hidden, 1 = shown
    const progress = useSharedValue(0)

    useImperativeHandle(ref, () => ({
      enable: () => setEnabled(true),
      disable: () => setEnabled(false),
    }))

    const focusInput = useCallback(() => {
      refTextInput.current?.focus()
    }, [])

    const animatedStyle = useAnimatedStyle(() => {
      const y = interpolate(progress.value, [0, 1], [tr, 0], Extrapolation.CLAMP)
      const op = interpolate(progress.value, [0, 1], [0, 1], Extrapolation.CLAMP)
      return {
        transform: [{ translateY: y }],
        opacity: op,
      }
    }, [tr])

    const open = useCallback(() => {
      // ничего не трогаем кроме анимации
      progress.value = withTiming(1, { duration: ANIM_DUR }, (finished) => {
        if (!finished) return
        runOnJS(focusInput)()
        if (callBackOnPress) runOnJS(callBackOnPress)()
      })
    }, [callBackOnPress, focusInput, progress])

    const close = useCallback(() => {
      progress.value = withTiming(0, { duration: ANIM_DUR }, (finished) => {
        if (!finished) return

        runOnJS(() => {
          // выключаем BackDrop и размонтируем ТОЛЬКО после анимации
          refBackDrop.current?.setStart(false)

          setSearchBarTopActive(false)
          setQuery('')
          onClose()
          setRender(false)
        })()
      })
    }, [onClose, progress, setSearchBarTopActive])

    // enabled -> mount/unmount flow
    useEffect(() => {
      if (enabled) {
        setSearchBarTopActive(true)
        setRender(true) // сначала смонтировать (чтобы BackDrop точно появился)
      } else {
        if (!render) {
          setSearchBarTopActive(false)
          return
        }
        close()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    // когда смонтировались и enabled=true -> включаем BackDrop и открываем
    useEffect(() => {
      if (!render || !enabled) return

      // ТУТ ref уже гарантированно есть
      refBackDrop.current?.setStart(true)

      open()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [render, enabled])

    // prevent jumps on breakpoint changes while hidden
    useEffect(() => {
      if (!render && !enabled) {
        progress.value = 0
      }
    }, [tr, render, enabled, progress])

    const {smartNavigate} = useSmartNavigate({navigation})
    const search = useCallback(() => {
      if (query.length > 0) {
        setEnabled(false)
        smartNavigate({
          navigation,
          goTo: 'SearchPage',
          data: { query },
        })
      } else {
        setError({ message: 'Введи запрос!!!' })
      }
    }, [navigation, query, smartNavigate])

    if (!render) return null

    return (
      <>
        <BackDrop ref={refBackDrop} onPress={() => setEnabled(false)} />

        <Animated.View
          style={[
            styles.container,
            { width: currentWidth, height: heightOfTopNav(isSmallVersion) },
            animatedStyle,
          ]}
        >
          <View
            style={[
              styles.inputContainer,
              {
                height: heightOfTopNav(isSmallVersion) - 8 * scaleFactor,
                paddingVertical: 8 * scaleFactor,
                paddingHorizontal: 12 * scaleFactor,
                borderRadius: 12 * scaleFactor,
              },
            ]}
          >
            <Search
              size={24 * scaleFactor}
              color={error ? errorColor : '#666'}
              style={styles.icon}
            />

            <TextInput
              ref={refTextInput}
              onSubmitEditing={search}
              placeholder={error ? error.message : 'Поиск...'}
              placeholderTextColor={error ? errorColor : '#777'}
              value={query}
              onChangeText={(text) => {
                if (error) setError(false)
                setQuery(text)
              }}
              style={[
                styles.input,
                {
                  fontSize: 16 * scaleFactor,
                  paddingLeft: 12,
                  paddingVertical: 6 * scaleFactor,
                  outlineStyle: 'none',
                } as any,
              ]}
            />

            <TouchableOpacity onPress={search}>
              <ViewSmooth
                style={[
                  styles.searchButton,
                  { width: 60 * scaleFactor, height: 30 * scaleFactor },
                ]}
              >
                <View
                  style={[
                    StyleSheet.absoluteFill,
                    { justifyContent: 'center', alignItems: 'center' },
                  ]}
                >
                  <Text style={{ color: 'white', fontSize: 14 * scaleFactor }}>
                    Поиск
                  </Text>
                </View>
              </ViewSmooth>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.closeButton, { padding: 8 * scaleFactor }]}
              onPress={() => setEnabled(false)}
            >
              <X size={28 * scaleFactor} color="#666" />
            </TouchableOpacity>
          </View>
        </Animated.View>
      </>
    )
  }
)

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    top: 0,
    backgroundColor: 'white',
    zIndex: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 6,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    paddingHorizontal: 12,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 5,
    elevation: 4,
  },
  input: {
    flex: 1,
    color: '#222',
    fontWeight: '500',
  },
  icon: {
    marginLeft: 8,
  },
  searchButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    marginHorizontal: 6,
  },
  closeButton: {
    marginHorizontal: 6,
  },
})

export default SearchBarTop
